import type { DeliveryType } from "@/types/orders";
import type { WarehouseNestedWithCoords } from "@/types/settings";

/**
 * Route status values as defined by the backend route lifecycle:
 * planned → active → completed → archived, with `cancelled` as a terminal
 * off-ramp (a disbanded route: stops detached, orders returned to the
 * unassigned pool; the row is NOT deleted).
 */
export type RouteStatus =
  | "planned"
  | "active"
  | "completed"
  | "archived"
  | "cancelled";

/**
 * A driver as nested by the route serializers — the same three fields on
 * `Route.driver`, `Route.resolved_driver` and `Route.vehicle.driver`.
 *
 * One named shape rather than three structurally-identical inline objects, so
 * that the substitution UI can pass any of them to the same renderer.
 */
export interface RouteDriverRef {
  id: number;
  first_name: string;
  last_name: string;
}

/** A vehicle as nested inside {@link RouteSubstitution}. */
export interface RouteVehicleRef {
  id: number;
  name: string;
}

/**
 * One half of a substitution: who/what is actually going out, and the standing
 * value from the SCHEDULE that is being deviated from.
 *
 * Both halves are nullable, and each null means something specific:
 * - `substitute: null` with a non-null `standing` — the override was CLEARED.
 *   The schedule staffed this route and it now has NOBODY on it. That IS a
 *   deviation and renders as one («водитель снят»), never as an ordinary route.
 * - `standing: null` — the schedule names nobody, so there is nothing to strike
 *   through; the marker renders without a replaced value rather than inventing
 *   one.
 */
export interface RouteSubstitutionHalf<T> {
  substitute: T | null;
  standing: T | null;
}

/**
 * TASK-4988/4989 — **the substitution, as the SERVER states it** (BUG-582).
 *
 * A substitution is a deviation from what the SCHEDULE says, and the schedule's
 * own driver/vehicle are not on this payload — so the fact is not derivable
 * client-side and the server computes it. Read THIS field and nothing else.
 *
 * Null in three distinct situations, all meaning "no deviation":
 * - the route is not schedule-born (`schedule == null`), so there are no
 *   standing values to deviate from;
 * - both halves match the schedule;
 * - the field is absent from an older cached payload.
 *
 * Each HALF is null when that half matches — a driver-only substitution emits
 * `vehicle: null` and never claims the vehicle was replaced.
 */
export interface RouteSubstitution {
  driver: RouteSubstitutionHalf<RouteDriverRef> | null;
  vehicle: RouteSubstitutionHalf<RouteVehicleRef> | null;
}

export interface Route {
  id: number;
  name: string;
  status: RouteStatus;
  date: string;
  created_at: string;
  /**
   * TASK-4736: when the dispatcher released this route to the driver, or null while it is still
   * a draft. A draft is invisible in the mobile app, so stops can be added and reordered without
   * the driver watching the plan change under them. On the BASE type, not RouteDetail, because
   * the routes LIST needs it for the row state and the bulk-release action.
   * Optional so older cached responses stay valid.
   */
  released_at?: string | null;
  /**
   * TASK-4920/4932 — the route's own driver, i.e. WHO THIS ROUTE IS ASSIGNED TO.
   *
   * **NOT the substitution marker — read `substitution` for that (BUG-582).**
   * This field was previously documented as the marker, on the premise that it
   * is written only by a substitution. That premise is false: the ORDINARY
   * schedule-materialisation path writes it too, starting from
   * `schedule.driver`, so every schedule-born route carries a non-null value
   * here and 11 dev routes rendered a «замена» against their own standing
   * driver.
   *
   * Never derive "is substituted" by comparing `resolved_driver` against
   * `vehicle.driver` either: that restates the server's fallback precedence in
   * the client, becoming a second definition that drifts the moment the
   * server's changes, and it false-positives when the vehicle has no standing
   * driver. Both objections stand — the answer is neither comparison but the
   * server-computed `substitution` field.
   */
  driver?: RouteDriverRef | null;
  /**
   * TASK-4988 — **THE substitution marker**, computed server-side against the
   * SCHEDULE's standing driver/vehicle (BUG-582).
   *
   * Optional, and `?? null` at every read: a payload cached before Sprint 198
   * omits it, which is "not substituted" — never an error and never a crash.
   */
  substitution?: RouteSubstitution | null;
  /**
   * TASK-4920 — WHO IS ACTUALLY DRIVING: the override when set, else the
   * vehicle's standing driver, else null. Computed server-side by
   * `resolve_route_driver`, so the client never reimplements the precedence.
   *
   * Render THIS as the driver everywhere. Optional so older cached payloads
   * (and pre-Sprint-191 fixtures) stay valid.
   */
  resolved_driver?: RouteDriverRef | null;
  /**
   * TASK-4920/4925 — whether saving a driver/vehicle change on this route
   * notifies the driver. Server-computed; today always `false`, because a
   * driver/vehicle PATCH writes no `RouteStop` and the only driver-facing push
   * is triggered from the RouteStop signals.
   *
   * Passed straight into `RecurringEditDialog`. **Never hardcode `false` in the
   * client**: the honest «позвоните водителю» copy must come from the server, so
   * that building a push later flips one computed field and every client follows.
   */
  driver_will_be_notified?: boolean;
  /**
   * TASK-4922 — the schedule that materialised this route, or null for an
   * ordinary dispatcher-built route.
   *
   * **`schedule !== null` is the ONE marker of a schedule-born route**, and it
   * is what gates the hold control (TASK-4931): an ordinary route has no
   * auto-release to skip, and offering the control there would imply one.
   */
  schedule?: number | null;
  /**
   * TASK-4931 — the dispatcher's one-off «придержать» on THIS date: auto-release
   * skips this route tomorrow while the schedule's standing rule is untouched.
   *
   * **SHIPPED.** `Route.release_hold` landed with TASK-4924
   * (`routes/models.py:165`, migration `0023`) and is exposed by two
   * serializers. Verified end-to-end against the live dev API on 2026-09-09:
   * present on GET, a PATCH persists, and the re-GET returns the new value.
   *
   * This comment previously said the field did NOT exist and that "the toggle
   * will not persist" — written while that was true, left behind when the
   * backend landed. It is kept optional because a payload from a pre-0023
   * deployment still omits it, and `releaseHoldState` reads absent as "not
   * held", never as an error.
   *
   * Distinct from `RouteSchedule.auto_release`, and the distinction is the whole
   * point of the control: this is ONE date's exception, `auto_release === false`
   * is the schedule having no auto-send AT ALL. For a single day they look
   * identical in outcome and mean entirely different things for the next.
   */
  release_hold?: boolean;
  vehicle?: {
    id: number;
    name: string;
    /** Plate, as shown beside the vehicle name in the filter modal's list. */
    registration_number?: string;
    /**
     * The VEHICLE's STANDING driver — not the route's. Since TASK-4920 a route
     * carries its own `driver` override and a server-resolved
     * `resolved_driver`; this field remains what it always was, and it is the
     * value rendered STRUCK THROUGH beside a substitute (TASK-4932).
     *
     * **Never written by the substitution flow.** Writing it would change every
     * route that vehicle has ever had, including past billed ones — the exact
     * defect per-date substitution exists to prevent.
     */
    driver?: RouteDriverRef | null;
    /**
     * TASK-4911: vehicle capacity, the DENOMINATOR of the detail page's ГРУЗ /
     * ОБЪЁМ bars. DRF `DecimalField`s → JSON **STRINGS** (`"1500.00"`,
     * `"20.000"`); parse before arithmetic.
     *
     * `"0"` means the field was NEVER CONFIGURED — it does not mean the vehicle
     * carries nothing. Dividing by it is a defect, so a zero renders the load
     * with no bar. The two fields go unset INDEPENDENTLY (dev vehicle #11 has
     * payload_kg 0 with volume_m3 20), so decide per FIELD, never per vehicle.
     * Optional so older cached payloads stay valid.
     */
    payload_kg?: string | null;
    volume_m3?: string | null;
  };
  stops_count: number;
  /**
   * TASK-4901: stops the driver CLOSED WITHOUT DELIVERING — `rejected` +
   * `skipped`. Feeds the «2 точки не доставлены» sub-line.
   *
   * Deliberately NOT derivable from `stops_count - stops_count_actuals_done`:
   * that counts `confirmed` as done too, so the subtraction yields UNRESOLVED
   * stops, not undelivered ones. A route with 2 genuinely rejected stops and
   * nothing pending would render «0 не доставлены» — an inverted number.
   * Optional so older cached payloads stay valid.
   */
  stops_count_undelivered?: number;
  /** Stops in a terminal state (confirmed + rejected + skipped) — i.e. RESOLVED, not delivered. */
  stops_count_actuals_done?: number;
  /** When the driver actually closed the route; drives «закрыт 17:12». */
  actual_end_at?: string | null;
  /**
   * The VEHICLE's shift start as «HH:MM» — NOT a route time.
   *
   * `RouteSerializer.get_start_time` returns `vehicle.shift_start`, so it is
   * null when the route has no vehicle or the vehicle has no shift configured.
   * It is NOT the route's departure (BUG-747) — that is `planned_departure_at`;
   * it remains only the fallback when the server has no departure. Do not read
   * it as the time the route actually started either — that is `actual_start_at`.
   */
  start_time?: string | null;
  /**
   * BUG-747: THE route departure, as a UTC ISO instant — the start-depot stop's
   * planned departure, falling back to `actual_start_at`; null when neither is
   * known. Format it in the tenant's timezone. Optional so older cached payloads
   * stay valid. Read through `routeDepartureSpan`, never re-derived.
   */
  planned_departure_at?: string | null;
  /**
   * Planned mileage in km. DRF `DecimalField` → arrives as a JSON **STRING**
   * (`"18.59"`), not a number, despite what this type claimed until now.
   *
   * `RouteSerializer` lists `planned_mileage_km` as a plain ModelSerializer
   * field, so it gets DRF's default decimal-as-string rendering. The `float()`
   * further down that serializer belongs to `get_distance_plan_km` — a
   * DIFFERENT, separately-named field — which is exactly why the wrong type
   * here looked verified.
   *
   * `string | number` matches the `total_weight_kg` / `total_volume_m3`
   * treatment: older cached payloads and test fixtures carry numbers. ALWAYS
   * `parseFloat`/`Number` before arithmetic or comparison — `18.59 !== "18.59"`
   * is what made RouteFormModal's PATCH diff permanently dirty for this field.
   *
   * NOTE this is the READ type. `CreateRoutePayload.planned_mileage_km` stays
   * `number | null`: that one goes OUT to the server, which wants a number.
   */
  planned_mileage_km: string | number | null;
  planned_duration_min: number | null;
  /**
   * TASK-5080 — how far behind (or ahead of) schedule this route is running
   * RIGHT NOW, in seconds: **positive is LATE, negative is EARLY**.
   *
   * **NOT YET SERVED BY THE BACKEND.** No route-level deviation exists on the
   * routes list today (verified against `RouteSerializer`: zero matches), so
   * this is `undefined` on every live payload and the «Отклонение» column
   * renders «—». The field is declared here because it is the shape the panel
   * ASKS FOR, and declaring it is what makes the client's expectation reviewable
   * instead of hidden inside a cast.
   *
   * The definition it must carry is the EXISTING one, not a second copy:
   * `public_api/services_eta.py`'s `deviation_sec()` — the ETA of the next
   * unvisited stop against its `scheduled_arrival_at` — with that module's
   * ±`ON_TIME_TOLERANCE_SEC` (300 s) dead band deciding the verdict. Three
   * deviation definitions already exist in this codebase; a fourth is how two
   * parts of the product start disagreeing about who is late.
   *
   * **`null`/absent means NOT MEASURED and must never be read as `0`.** A route
   * with no schedule has no contract to deviate from (`scheduled_*` is NULL on
   * any route not born from a schedule), so it legitimately has no deviation —
   * and painting it «по плану» would assert punctuality nobody checked.
   */
  deviation_sec?: number | null;
  /**
   * TASK-5080 — the server's editorial verdict on {@link deviation_sec}, so the
   * client never re-derives one from the raw number against a threshold it
   * would have to hardcode a second time.
   *
   * Same status as its sibling: NOT YET SERVED. Values mirror
   * `public_api.services_eta.ScheduleStatus`.
   */
  schedule_status?: "on_time" | "late" | "early" | null;
  /**
   * Aggregated load in kg. On the BASE type, not just `RouteDetail`: the LIST
   * endpoint sends it too — verified live against `/api/v1/routes/`, whose
   * payload carries `total_weight_kg` on every row. It was simply absent here,
   * so the routes-map surfaces could not read a field they were already being
   * given.
   *
   * DRF `DecimalField` → a JSON **STRING** (`"10.500"`); `string | number`
   * because older cached payloads and test fixtures use numbers. ALWAYS
   * `Number()`/`parseFloat` before arithmetic — `"8.5" + "0.1"` concatenates
   * to `"8.50.1"`, the documented trap on this project.
   */
  total_weight_kg?: string | number | null;
  color?: string | null;
  /**
   * Resolved GPS origin the route was planned from (BUG-442).
   *
   * NON-NULL means the solver was anchored on the vehicle's live position
   * instead of the start warehouse, so the drawn line legitimately begins in
   * open road and the map shows a distinct origin marker there. NULL — the
   * common case, and every route built before this field existed — means the
   * route is warehouse-anchored and nothing extra is drawn.
   *
   * Serialized by DRF as a `DecimalField`, i.e. a JSON STRING such as
   * `"53.677000"` — always convert with `Number()` before any arithmetic or
   * `toFixed` (see `parseRouteStart`).
   */
  start_lat?: string | null;
  start_lon?: string | null;
}

/**
 * Extended route shape returned by the detail endpoint.
 * Adds warehouse, actual timestamps, and aggregated weight/volume/cost fields.
 */
export interface RouteDetail extends Route {
  warehouse: {
    id: number;
    name: string;
    lat: string | null;
    lon: string | null;
  } | null;
  /**
   * Mid-route warehouse (depot stop inserted between order stops).
   * Added in Sprint 64. Null if not configured for this route.
   */
  mid_warehouse: WarehouseNestedWithCoords | null;
  /**
   * End warehouse (return depot at the end of the route).
   * Added in Sprint 64. Null if not configured for this route.
   */
  end_warehouse: WarehouseNestedWithCoords | null;
  /**
   * True when the start warehouse and end warehouse are the same object.
   * Computed by the backend on save. Added in Sprint 64.
   */
  is_circular: boolean;
  actual_start_at: string | null;
  actual_end_at: string | null;
  /**
   * Aggregated load. DRF `DecimalField` → these arrive as JSON **STRINGS**
   * (`"13.500"`), not numbers, despite what this type claimed until Sprint 189.
   * `string | number` because older cached payloads and test fixtures use
   * numbers; ALWAYS `parseFloat`/`Number` before arithmetic — `"8.5" + "0.1"`
   * concatenates to `"8.50.1"`, which is the documented trap on this project.
   */
  total_weight_kg: string | number | null;
  total_volume_m3: string | number | null;
  total_cost: string | null;
  delivery_strategy: string;
  visit_order: "strict" | "any";
  /**
   * Actually driven kilometres, summed from GPS pings inside the route's
   * actual window (`TrackingService.compute_actual_distance_km`, exposed as a
   * `SerializerMethodField` on `RouteSerializer` — verified against the running
   * backend, Sprint 201).
   *
   * A real JSON **number**, not a decimal string: the method returns a rounded
   * `float`, so unlike `planned_mileage_km` it needs no `parseFloat`.
   *
   * **`null` is the ordinary case, not an error.** The backend returns null
   * when the route has no `actual_start_at`, has no assigned vehicle, or has
   * fewer than two GPS pings in the window. A consumer must render its empty
   * state on null and must never substitute `0` — "the van drove nowhere" and
   * "nobody measured" are different facts.
   *
   * Optional so older cached payloads and existing test fixtures stay valid.
   */
  distance_actual_km?: number | null;
}

/**
 * Stop lifecycle values, mirroring `RouteStop.Status` on the backend
 * (core/src/apps/routes/models.py:152).
 *
 * `in_progress` is the driver's "en route to this stop" state. It was missing
 * from this union until Sprint 189 even though the backend has always emitted
 * it: the omission made every `Record<RouteStopStatus, …>` style/colour map
 * silently return `undefined` for a live stop, and `t("routes.stopStatus.in_progress")`
 * had no key in either locale, so the status dropdown rendered the raw key
 * path. Dev route 10978 carries a stop in exactly this state.
 */
export type RouteStopStatus =
  | "pending"
  | "in_progress"
  | "visited"
  | "confirmed"
  | "rejected"
  | "skipped";

export interface RouteStop {
  id: number;
  route_id: number;
  order_id: number | null;
  order_title: string | null;
  order_external_id: string | null;
  order_address: string | null;
  /** Recipient name — shown when the stop has no title/external id, so a row is never just «—». */
  order_customer_name?: string | null;
  order_lat: string | null;
  order_lon: string | null;
  position: number;
  status: RouteStopStatus;
  planned_arrival_at: string | null;
  planned_departure_at: string | null;
  /**
   * "Per contract" — the time promised to the customer, copied from the source
   * card's time window when a schedule-born route is created (TASK-4962) and
   * never touched by a recalculation. Serialized by `RouteStopSerializer`
   * (`core/src/apps/routes/serializers.py:171`).
   *
   * NULL on every route not born from a schedule, and that NULL is meaningful —
   * "no contract" — so it must never be backfilled from `planned_arrival_at`
   * (`core/src/apps/routes/models.py:291`). The ОТКЛОНЕНИЕ column prefers this
   * field and labels the row accordingly; see
   * `@/features/routes/lib/stopDeviation` for the TASK-5022 ruling.
   */
  scheduled_arrival_at?: string | null;
  scheduled_departure_at?: string | null;
  /**
   * Seconds by which a STRICT window's close was overrun by `planned_arrival_at`,
   * or null when there is nothing to report — which is the overwhelming majority
   * of stops (no strict window, no arrival, an arrival inside the window).
   *
   * Server-computed in the TENANT's zone by `strict_window_overrun_sec`
   * (`core/src/apps/routes/services.py:1471`, BUG-598) and serialized by
   * `RouteStopSerializer` (`core/src/apps/routes/serializers.py:284`). Verified
   * live on the wire 2026-10-06.
   *
   * Declared for TASK-5055, and it is the ONLY strictness signal a client has:
   * `RouteStopNestedOrderSerializer` does not expose `strict_time_window` at
   * all, so a non-null value here is what distinguishes a broken HARD promise
   * from a soft miss the solver was allowed to make. Positive-only and
   * LATE-only — the early direction has its own field below, rather than a
   * negative value here, because three readers already treat this one as
   * "seconds late" and would render «на −5880 мин позже» the day an early plan
   * appeared (BUG-638 in a second place).
   */
  strict_window_overrun_sec?: number | null;
  /**
   * Seconds by which `planned_arrival_at` falls BEFORE a STRICT window's
   * OPENING, or null when there is nothing to report. Positive ("early by N").
   *
   * BUG-638 / TASK-5062 — the mirror of `strict_window_overrun_sec`, computed in
   * the TENANT's zone by `strict_window_early_sec`
   * (`core/src/apps/routes/services.py`).
   *
   * Rare but REACHABLE, which is why it exists rather than being pinned as
   * impossible: the BUG-129 clamp pushes a strict arrival forward to its window
   * opening on the ordinary multi-stop ETA path, but the two single-stop
   * short-circuits write `planned_arrival_at` with no clamp at all, and a stop's
   * `scheduled_arrival_at` outranks the order's own window when the two
   * disagree. Together with the field above this is the full strictness signal:
   * `stopWindowViolation` reads whichever matches the direction it computed.
   */
  strict_window_early_sec?: number | null;
  /**
   * SIGNED seconds a DEPOT stop's planned visit falls outside its WAREHOUSE's
   * opening hours, or null when there is nothing to report.
   *
   * **negative = BEFORE `work_start`** (the van is loaded before the warehouse
   * opens); **positive = AFTER `work_end`** (it returns after close). Both
   * boundaries are INSIDE — exactly at the opening or the close is on time.
   * The same sign convention as the order-window fields above, deliberately, so
   * one vocabulary covers an order's promise and a depot's hours.
   *
   * Server-computed in the TENANT's zone by `depot_window_deviation_sec`
   * (`core/src/apps/routes/services.py`), and **not re-derived on the client**:
   * which instant is judged differs by depot kind — the START depot on its
   * DEPARTURE (a van does not arrive where it starts, BUG-656), every other
   * depot on its ARRIVAL — and a second definition of that rule would be free
   * to disagree with the badge beside it.
   *
   * `null` covers an order stop, a depot with no warehouse, a warehouse with
   * neither bound configured (the overwhelmingly common case), a missing planned
   * instant, and a route with no date.
   *
   * Why it can be non-null at all: the depot's hours are a SOFT window (owner,
   * 2026-10-12 «я думаю мягкая пока что»), so the solver is ALLOWED to plan
   * outside them — and after TD-014 it DOES, rather than dropping the order, so
   * this field is the only thing that makes the trade-off visible.
   */
  depot_window_deviation_sec?: number | null;
  actual_arrival_at: string | null;
  actual_departure_at: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
  /**
   * Nested order summary for order stops; null for depot stops.
   *
   * ## This type was under-declared, and it cost a sprint round
   *
   * It previously listed only `id` and `delivery_type`, which led a previous
   * pass to conclude — from the TYPE rather than from the API — that the
   * mockup's «окно 09:00—12:00 · 22 кг» could not be built, and to ship a
   * comment saying so. The backend's `RouteStopNestedOrderSerializer`
   * (`core/src/apps/routes/serializers.py:55`) has always returned all
   * thirteen fields below; verified live on route 10978.
   *
   * The lesson worth keeping: a hand-maintained TS interface is a CLAIM about
   * the API, not evidence of it. Widening it here is what makes the window and
   * weight reachable at all.
   *
   * `parcels` is deliberately NOT in this list — it is the one field the mockup
   * asks for that this serializer genuinely does not send (it exists on
   * `/v1/orders/`, but not here). See {@link RouteStop.order} usage in
   * `RouteMapPanel`, which omits the parcel count rather than faking it.
   */
  order: {
    id: number;
    /**
     * The permanent order (`OrderTemplate`) this working order was materialized
     * from, or null for an ordinary order (BUG-668,
     * `RouteStopNestedOrderSerializer`). The add-order picker reads it to mark a
     * permanent order that is already on the route.
     */
    source_template_id?: number | null;
    delivery_type: DeliveryType;
    customer_name?: string | null;
    customer_phone?: string | null;
    customer_phone_2?: string | null;
    order_type?: string | null;
    /** DECIMAL AS STRING (e.g. "22.000") — parse before arithmetic, never concatenate. */
    weight_kg?: string | null;
    /** DECIMAL AS STRING (e.g. "0.100") — parse before arithmetic. */
    volume_m3?: string | null;
    /** DECIMAL AS STRING. */
    item_cost?: string | null;
    /** «HH:MM:SS» or null when the order has no delivery window. */
    time_window_start?: string | null;
    /** «HH:MM:SS» or null when the order has no delivery window. */
    time_window_end?: string | null;
    notes?: string | null;
    service_time_min?: number | null;
  } | null;
  /** Delivery confirmation evidence attached to this stop; null when not yet confirmed. */
  confirmation: {
    photo_url: string | null;
    signature_url: string | null;
    has_photo: boolean;
    has_signature: boolean;
    confirmed_at: string;
    receiver_name: string;
    note: string;
    signed_at: string | null;
  } | null;
  /**
   * Stop type discriminator. Added in Sprint 64.
   * - 'order': a regular delivery/pickup order stop (default for all pre-existing stops).
   * - 'start_depot': start warehouse stop at position 0.
   * - 'mid_depot': mid-route warehouse stop inserted between order stops.
   * - 'end_depot': end warehouse stop at the final position.
   */
  stop_type: "order" | "start_depot" | "mid_depot" | "end_depot";
  /**
   * Warehouse reference for depot stops. Null for order stops.
   * Includes coordinates for map rendering. Added in Sprint 64.
   */
  warehouse: WarehouseNestedWithCoords | null;
  /**
   * Whether delivery confirmation is required at this stop.
   * Computed by the backend: order-level value if set, else TenantPlanningSettings fallback.
   * Added in Sprint 64.
   */
  require_confirmation: boolean;
  /**
   * Confirmation evidence mode required at this stop.
   * Computed by the backend with the same fallback logic as require_confirmation.
   * Added in Sprint 64.
   */
  confirmation_mode: "photo" | "signature" | "photo_and_signature" | "none";
}

export interface CreateRoutePayload {
  name: string;
  date: string;
  vehicle: number | null;
  /**
   * Start warehouse (depot) ID. Maps to Route.warehouse on the backend.
   *
   * TASK-4906 — DO NOT rename these three to `*_id`. `POST /v1/routes/` and
   * `PATCH /v1/routes/{id}/` are both served by `RouteCreateUpdateSerializer`,
   * where the writable fields are `PrimaryKeyRelatedField`s named EXACTLY
   * `warehouse` / `mid_warehouse` / `end_warehouse` — no suffix. The `*_id`
   * spelling belongs to a DIFFERENT endpoint (`RouteBuildSerializer`, see
   * {@link BuildRoutePayload}), and DRF ignores unknown keys SILENTLY: the
   * request still answers 201/200 and quietly discards every warehouse. That
   * shipped once and read like a backend limitation; it was this type.
   *
   * OPTIONAL since BUG-542, and the optionality is load-bearing: the three warehouse
   * slots are THREE-valued on the wire. Present with an id pins that depot; present as
   * `null` means «Нет склада» and is honoured as-is; ABSENT means "no opinion" and lets
   * the backend apply the tenant default. Sending `null` where you meant absent is the
   * exact defect BUG-542 records — the company's configured depot then never applies.
   */
  warehouse?: number | null;
  /**
   * Mid-route warehouse ID, or null to clear. Optional write field added in Sprint 64.
   * When absent from the payload the backend uses TenantPlanningSettings.default_mid_warehouse.
   */
  mid_warehouse?: number | null;
  /**
   * End warehouse ID, or null to clear. Optional write field added in Sprint 64.
   * When absent from the payload the backend uses TenantPlanningSettings.default_end_warehouse.
   */
  end_warehouse?: number | null;
  /**
   * Route colour as a hex string. OPTIONAL: the backend model already carries
   * `default="#3B82F6"`, and the column is not `blank=True`, so sending `""`
   * answers 400 "This field may not be blank" — omit the key instead and let
   * the server apply its default (found live in TASK-4907; tsc/eslint/vitest
   * were all green while the create silently failed).
   */
  color?: string;
  planned_mileage_km: number | null;
  planned_duration_min: number | null;
  visit_order: "strict" | "any";
}

export interface BuildRoutePayload {
  order_ids: number[];
  strategy: "manual" | "auto";
  /** Planned execution date for the route (YYYY-MM-DD). */
  date: string;
  vehicle_id?: number;
  visit_order: "strict" | "any";
  warehouse_id?: number | null;
  mid_warehouse_id?: number | null;
  end_warehouse_id?: number | null;
}

export interface UpdateRouteStopPayload {
  status?: RouteStopStatus;
  actual_arrival_at?: string | null;
  actual_departure_at?: string | null;
  notes?: string;
}

/**
 * Standard DRF paginated list response shape.
 */
export interface PaginatedResponse<T> {
  count: number;
  results: T[];
}

/**
 * Whole-queryset Sum/Count aggregates the Routes list endpoint attaches for the
 * map KPI capsule (TASK-4424). Computed over the tenant-scoped, filter-respecting
 * queryset BEFORE pagination, and — unlike `status_counts` — the `status` filter
 * IS applied, so a status selection narrows these totals.
 *
 * Counts (`stops`, `duration`) arrive as integers; decimal sums (`distance`,
 * `weight`, `volume`, `pallets`) arrive as fixed-precision STRINGS (e.g.
 * `"10.000"`) from DRF `DecimalField`. Always parse with `Number(...)` and guard
 * `NaN → 0` before rendering. `duration` is in minutes.
 */
export interface RouteSums {
  /** Total number of stops across all routes (Count("stops")). */
  stops: number;
  /**
   * Stops closed WITHOUT delivery across the whole filtered queryset
   * (BUG-528): `rejected` + `skipped`. The aggregate twin of the per-route
   * `stops_count_undelivered`; `confirmed` is a SUCCESS and is never counted.
   *
   * A JSON NUMBER, not a decimal string — it comes from a `Count`, unlike the
   * `Sum`-derived `distance`/`weight`/`volume`/`pallets` siblings below, which
   * arrive as strings. Do NOT `parseFloat` it.
   *
   * Always present (`0` when nothing failed, never omitted), and computed
   * before pagination under the same scoping as its siblings.
   */
  undelivered: number;
  /**
   * Stops DELIVERED across the whole filtered queryset (TASK-5082):
   * `Count("stops", filter=Q(stops__status="confirmed"))`. The success twin of
   * `undelivered`, computed in the same pre-pagination pass.
   *
   * A JSON NUMBER like `undelivered`, not a decimal string — do NOT `parseFloat`.
   * Always present (`0` on a day with no deliveries yet, never omitted), so a
   * missing key means an OLD server, not a zero day.
   */
  delivered: number;
  /**
   * Of `delivered`, how many arrived AFTER their window closed (TASK-5082).
   *
   * A SUBSET of `delivered` — a late delivery is still a delivery — so the
   * on-time count is `delivered - delivered_late`, never a third sibling.
   * Deliberately its own filtered count rather than derived from
   * `otd_percent`: that percentage runs over a different denominator (stops
   * with a window AND an arrival), so multiplying it back out would invent a
   * number.
   *
   * A stop missing a window or an arrival is not counted as late.
   */
  delivered_late: number;
  /** Sum of planned_mileage_km, decimal string (km). */
  distance: string;
  /** Sum of planned_duration_min, integer minutes (or null when empty). */
  duration: number | null;
  /** Sum of related orders' weight_kg, decimal string (kg). */
  weight: string;
  /** Sum of related orders' volume_m3, decimal string (m³). */
  volume: string;
  /** Sum of related orders' pallets, decimal string. */
  pallets: string;
}

/**
 * The six FILTER-CHIP keys the routes list aggregates over (TASK-4901).
 *
 * These are NOT the five DB statuses. «Черновик» (`draft`) and «Отправлен»
 * (`sent`) are BOTH `planned`, split by `released_at`: a draft the driver
 * cannot see yet vs one already sent. The mockup's «Запланирован» chip is that
 * same `planned` set a third time, so it is deliberately absent (owner decision
 * 3, 2026-08-26) — these six are DISJOINT and sum to the route total, which is
 * what lets a chip count be checked against it.
 *
 * The backend resolves both the `status` FILTER and these counts through one
 * shared map (`CHIP_STATUS_FILTERS`, `core/src/apps/routes/filters.py`), so a
 * chip's count always equals the number of rows selecting it returns.
 */
export type RouteChipStatus =
  | "draft"
  | "sent"
  | "active"
  | "completed"
  | "cancelled"
  | "archived";

/**
 * Per-chip route counts over the whole filtered queryset, before pagination.
 *
 * Computed with the `status` param EXCLUDED, so each value describes what that
 * chip WOULD yield if selected — a chip never reads 0 merely because a
 * different status is currently selected. A status with no matches is present
 * with `0`, never omitted.
 *
 * NOTE: there is no `planned` key. It was removed in TASK-4901 when the six
 * chip keys replaced the three DB statuses; `planned` is now `draft + sent`.
 * Reading `status_counts.planned` silently yielded `undefined` — and with the
 * `?? 0` the old call sites used, it rendered a confident, wrong `0` that no
 * type check, lint rule or unit test could see. Typing the real key set is what
 * makes that class of defect a compile error instead.
 */
export type RouteStatusCounts = Record<RouteChipStatus, number>;

/**
 * Routes list response. Extends the standard paginated shape with whole-
 * queryset status aggregates (computed before pagination, over the current
 * date-range filter). Backend always returns `status_counts`; read it
 * defensively with `?? 0` for older cached payloads or the loading state.
 * `sums` (TASK-4424) feeds the map KPI capsule; read it defensively too.
 */
export interface PaginatedRoutes extends PaginatedResponse<Route> {
  status_counts: RouteStatusCounts;
  sums?: RouteSums;
}

/**
 * An order that has no RouteStop on any route for a given delivery date.
 * Returned by GET /api/v1/routes/unassigned-orders/?date=YYYY-MM-DD
 */
export interface UnassignedOrder {
  id: number;
  title: string;
  /** Optional order number / external identifier, when the order has one. */
  external_id: string | null;
  /** Recipient name — the fallback label when there is no external id or title. */
  customer_name?: string | null;
  address: string;
  lat: string | null;
  lon: string | null;
  delivery_date: string;
  /**
   * Delivery facts, mirroring the nested `order` on {@link RouteStop}.
   *
   * ALL OPTIONAL, deliberately. `UnassignedOrderSerializer` historically sent
   * only the eight fields above (verified live), which is why the unassigned
   * card could show no window and no weight while the in-route card could. A
   * backend change is adding these; typing them optional means the card renders
   * them the moment they arrive and simply omits the line until then — no
   * client release has to be coordinated with the server one, and an older
   * cached response stays valid.
   *
   * Decimals arrive as STRINGS (`"12.000"`) exactly as on `RouteStop.order` —
   * parse before arithmetic, never concatenate.
   */
  weight_kg?: string | null;
  volume_m3?: string | null;
  /** «HH:MM:SS» or null when the order has no delivery window. */
  time_window_start?: string | null;
  time_window_end?: string | null;
  /** Number of scannable parcels; the in-route card cannot show this yet. */
  parcels_count?: number | null;
}

/**
 * Payload for moving a stop from one route to another (or assigning an
 * unassigned order to a route).
 * POST /api/v1/routes/move-stop/
 */
export interface MoveStopPayload {
  order_id: number;
  from_route_id: number | null;
  to_route_id: number;
  position?: number;
}

/**
 * Response returned by POST /api/v1/routes/move-stop/.
 * Contains updated stop lists for both affected routes.
 */
export interface MoveStopResponse {
  from_route_stops: RouteStop[];
  to_route_stops: RouteStop[];
}

/**
 * Payload for POST /api/v1/planning/run/.
 * Triggers async route optimization via Celery.
 */
export interface RunOptimizationPayload {
  order_ids: number[];
  vehicle_ids: number[];
  warehouse_id: number;
}

/**
 * Response from POST /api/v1/planning/run/.
 * The task runs asynchronously; poll /api/v1/planning/result/{task_id}/ for status.
 */
export interface RunOptimizationResponse {
  task_id: string;
  status: string;
}

/**
 * Response from POST /api/v1/routes/build/ (HTTP 202).
 * The route build runs asynchronously; poll /api/v1/routes/build/result/{task_id}/ for status.
 */
export interface BuildRouteTaskResponse {
  task_id: string;
  status: "pending" | "running" | "completed" | "failed";
}

/**
 * The solver exclusion codes classified by
 * `core/src/apps/planning/solver.py` `_diagnose_drop`.
 *
 * Each has a matching `routes.buildAlert.reasons.*` / `routes.buildAlert.suggestions.*`
 * entry in BOTH locale files — that mapping is the single translation of these
 * codes on the client (the backend deliberately no longer sends prose, TASK-4740).
 */
export const UNASSIGNED_REASON_CODES = [
  "capacity",
  "time_window",
  "max_duration",
  "max_distance",
  "max_orders",
  "requirements",
  "no_vehicle",
  "unroutable",
  // BUG-545. Adding a code here is NOT optional bookkeeping: this list gates
  // translation (`isKnownUnassignedReason`), so a code missing from it renders as
  // «причина: shift» via reasons.unknown and drops its suggestion line entirely —
  // even though both locale files carry the strings. tsc cannot catch that (the
  // reason field is typed `string`) and check:i18n cannot either (the key is built
  // dynamically), so only a rendered-text test will.
  "shift",
  "undetermined",
  // BUG-677 (backend BUG-675): a van could serve the order alone but every such van
  // is already full; and an idle van could serve it but the tariff's routes-per-day
  // quota forbids opening another route.
  "no_room",
  "routes_quota",
  // BUG-679: a STRICT window no model can contain is screened out before the solve
  // (an empty range used to fail the whole build with «CP Solver fail»): the window
  // closes before it opens, or it opens after every free van's shift has ended.
  "window_invalid",
  "window_after_shift",
  // BUG-680: only when the placement pass hit its safety cap with feasible orders left
  // and idle vans still free — an honest «time ran out», never «undetermined».
  "solver_time_limit",
  // BUG-713: replaces `solver_time_limit` for new builds — an idle van could serve the
  // order but the planner failed to place it (no construction finished, or the
  // completion's safety cap ran out). Not a tenant setting, so the advice never says
  // «raise the optimizer time». `solver_time_limit` stays for stored results.
  "planner_failed",
  // BUG-685: every van that could serve the order is on a route that, with the order
  // inserted at its cheapest place, would end after the van's day (its shift end, or
  // 24:00 when it has none) — the day is full, not the cause «undetermined».
  "day_full",
  // BUG-696: every van able to carry the order has already finished today's shift
  // ({shift_end, now}) — said plainly instead of an invented «return at» time.
  "shift_over",
  // BUG-686: picks the backend SKIPS before planning because they cannot be planned
  // at all (a 1000-order build used to be refused whole for one of them). Worded in
  // `@/lib/ineligiblePicks`, shared with the ROUTE_ORDER_NOT_ELIGIBLE refusal.
  "already_routed",
  "order_archived",
  "order_terminal",
  "order_missing",
  // BUG-687…693 (owner, 2026-10-20 — «сохранить всё, кроме изменённого»): the build
  // writes everything that did not change while it ran; an order edited meanwhile
  // ({fields}) or an order whose planned van was taken/changed ({vehicle_id,
  // vehicle_name}) comes back with one of these. Worded in `@/lib/buildChanges`.
  "order_changed_during_build",
  "vehicle_changed_during_build",
] as const;

/** A solver exclusion reason the frontend knows how to translate. */
export type UnassignedReasonCode = (typeof UNASSIGNED_REASON_CODES)[number];

/** True when `value` is one of the codes the locale files translate. */
export function isKnownUnassignedReason(
  value: string,
): value is UnassignedReasonCode {
  return (UNASSIGNED_REASON_CODES as readonly string[]).includes(value);
}

/**
 * One order the solver refused while it was being ADDED to an existing route
 * (BUG-668) — an item of `unplaced` in `batch_add_orders`' `include_unplaced`
 * response, and of `extra_data.unplaced` on a 409 `ROUTE_ORDER_UNPLACEABLE`.
 *
 * `order_id` is the id the caller sent in `order_ids` (null for a permanent
 * order); `template_id` the id sent in `template_ids` (null otherwise).
 * `reason` is a raw solver code (see {@link UNASSIGNED_REASON_CODES}) or `""`.
 */
export interface UnplacedRouteAdd {
  order_id: number | null;
  template_id: number | null;
  reason: string;
  order_title: string | null;
  order_address: string | null;
  /** "HH:MM" or null. */
  time_window_start: string | null;
  /** "HH:MM" or null. */
  time_window_end: string | null;
}

/** `batch_add_orders` 200 body when `include_unplaced: true` is sent. */
export interface BatchAddOrdersResult {
  stops: RouteStop[];
  unplaced: UnplacedRouteAdd[];
}

/** An order that could not be assigned to any route during auto-build. */
export interface UnassignedBuildOrder {
  order_id: number;
  reason: UnassignedReasonCode;
  order_title: string | null;
  order_address: string | null;
  /** Recipient name — shown when the stop has no title/external id, so a row is never just «—». */
  order_customer_name?: string | null;
  /** Measured figures behind the reason, when the backend supplies them. See {@link UnassignedReasonDetail}. */
  detail?: UnassignedReasonDetail | null;
  /** See {@link UnassignedPermanentProvenance}. */
  from_permanent?: boolean;
  template_id?: number | null;
}

/**
 * One dropped order as it arrives inside a route-build notification's
 * `extra_data.unassigned` (TASK-4740).
 *
 * `reason` is deliberately a plain `string`, NOT `UnassignedReasonCode`: the
 * backend forwards the RAW solver code untranslated, so a solver version that
 * adds a 9th code delivers a value this build has never heard of. Typing it as
 * the closed union would make the compiler promise something the wire does not,
 * and the unknown-code fallback would look like dead code.
 */
export interface UnassignedNotificationOrder {
  order_id: number;
  reason: string;
  order_title?: string | null;
  order_address?: string | null;
  /** Measured figures behind the reason, when the backend supplies them. */
  detail?: UnassignedReasonDetail | null;
  /** See {@link UnassignedPermanentProvenance}. */
  from_permanent?: boolean;
  template_id?: number | null;
}

/**
 * BUG-685: present (`from_permanent: true` + the permanent order's `template_id`)
 * when the dropped order was this build's copy of a PERMANENT order. Having reached
 * no route, that copy has been deleted (BUG-546), so the dispatcher has to be told
 * to place it again — otherwise the order silently disappears for that date.
 */
export interface UnassignedPermanentProvenance {
  from_permanent?: boolean;
  template_id?: number | null;
}

/**
 * Optional measured figures explaining WHY one order was dropped (BUG-675/677),
 * per reason as `solver.py` `_diagnose_drop` writes them (times are `HH:MM`,
 * tenant-local):
 *
 * | reason         | detail                                                   |
 * | time_window    | `{earliest_arrival, window_start, window_end}`           |
 * | shift          | `{return_at, shift_end}` or `{needed_min, longest_shift_min}` |
 * | shift_over     | `{shift_end, now}` (BUG-696)                             |
 * | max_distance   | `{needed_km, limit_km}`                                  |
 * | max_duration   | `{needed_min, limit_min}`                                |
 * | max_orders     | `{limit}`                                                |
 * | capacity       | `{weight_kg, volume_m3}`; an order too big for every van adds `max_payload_kg`, `max_volume_m3` (largest van per enforced dimension, may be `null`) (BUG-718) |
 * | no_room        | `{vehicles_able}`                                        |
 * | routes_quota   | `{limit, used}` or, from the vehicle cap, `{routes_allowed}` |
 * | window_invalid | `{window_start, window_end}` (BUG-679)                   |
 * | window_after_shift | `{window_start, window_end, latest_shift_end}` (BUG-679) |
 * | solver_time_limit | `{idle_vans}` (BUG-680; legacy since BUG-713)           |
 * | planner_failed | `{idle_vans}` (BUG-713)                                  |
 * | day_full       | `{return_at, return_next_day, day_end, day_end_next_day}` (BUG-685) |
 * | already_routed | `{route_id, route_name, route_date}` (BUG-686)          |
 * | order_archived | `{status: "archived"}` (BUG-686)                         |
 * | order_terminal | `{status}` — visited/confirmed/rejected/skipped (BUG-686) |
 * | order_changed_during_build | `{fields: string[]}` — plan fields or `["status"]` |
 * | vehicle_changed_during_build | `{vehicle_id, vehicle_name}` |
 * | undetermined / unroutable / requirements / no_vehicle / order_missing | none |
 *
 * `time_window` / `shift` may add `next_night: true` + `vehicle_name` (BUG-694): the
 * window is the night AFTER the route date, judged against that night van. Any
 * measured detail may add `estimated: true` (BUG-691): the routing service was down
 * and the figures are a straight-line estimate.
 *
 * Kept an open record: every reader (`describeUnassignedDetail`) narrows each
 * field at runtime and renders nothing for a shape it does not recognize.
 */
export type UnassignedReasonDetail = Record<string, unknown>;

/**
 * The bounded unassigned breakdown carried by a route-build notification's
 * `extra_data` (TASK-4740). Built by
 * `core/src/apps/routes/services.py` `build_unassigned_extra_data()`.
 */
export interface UnassignedBreakdownPayload {
  /** Total dropped orders — NOT capped, so the UI can say "and N more". */
  unassigned_total: number;
  /** True when `unassigned` was capped and does not list every dropped order. */
  unassigned_truncated: boolean;
  /**
   * Per-reason tally over EVERY dropped order, including those past the item
   * cap — so a truncated item list still yields a truthful breakdown.
   */
  unassigned_reason_counts: Record<string, number>;
  /** The itemized orders, capped by the backend (currently at 20). */
  unassigned: UnassignedNotificationOrder[];
}

/**
 * Per-delivery-date summary of a completed auto-build. The backend partitions
 * the selected orders by delivery date: overdue orders (delivery_date < today)
 * are built onto TODAY's date, while future orders are built onto their own
 * delivery date. One entry is produced per resulting date group.
 */
export interface BuildDateSummary {
  /** ISO date of this group, e.g. "2026-07-12". */
  date: string;
  /** IDs of the routes created for this date. */
  route_ids: number[];
  /** Number of routes created for this date. */
  route_count: number;
  /** Number of orders actually PLACED on this date's routes (review round 2). */
  order_count: number;
  /**
   * Number of selected orders grouped onto this date (placed or not). Absent on
   * payloads written before review round 2.
   */
  picked_count?: number;
  /**
   * How many orders in this group were pulled forward from a past
   * delivery_date. Non-zero only on the today-group.
   */
  overdue_order_count: number;
}

/**
 * A route as the build result returns it: the serialized route plus, from the
 * build only (it is NOT stored on the route, so the route endpoints never send
 * it), `eta_estimated: true` when it was planned on a straight-line estimate
 * because the road routing service was unreachable (BUG-691).
 */
export type BuiltRoute = RouteDetail & { eta_estimated?: boolean };

/**
 * Response from GET /api/v1/routes/build/result/{task_id}/.
 * Poll every 2 seconds until status is "completed" or "failed".
 */
export interface BuildRouteResultResponse {
  task_id: string;
  status: "pending" | "running" | "completed" | "failed";
  progress_pct: number;
  tenant_id: number;
  result: {
    routes: BuiltRoute[];
    unassigned: UnassignedBuildOrder[] | null;
    /**
     * Per-delivery-date breakdown of the build (overdue → today, future →
     * own date). Optional/nullable for older cached payloads.
     */
    by_date?: BuildDateSummary[] | null;
    /**
     * BUG-691: coded warnings of the completed build, e.g.
     * `{code: "PLANNING_MATRIX_ESTIMATED", params: {route_count}}` when the road
     * routing service was down and times/km are a straight-line estimate.
     * Absent on older payloads.
     */
    warnings?: { code: string; params?: Record<string, unknown> | null }[];
  } | null;
  error: string | null;
  /**
   * BUG-672: registry error code of a `failed` task (e.g. `ROUTE_BUILD_STALE`,
   * `ROUTE_ORDER_UNPLACEABLE`, `VALIDATION_ERROR`, `INTERNAL_ERROR`). Absent on
   * older payloads and on non-failed statuses.
   */
  code?: string | null;
  /**
   * BUG-674: the figures behind `code`, when it has any — `ROUTE_QUOTA_EXHAUSTED`
   * sends `{limit, used, date}`. Absent for every other code.
   */
  params?: Record<string, unknown> | null;
}

/** Load-check strictness mode resolved from the tenant planning settings. */
export type LoadCheckMode = "off" | "warn" | "strict";

/**
 * Per-order load-check breakdown returned by the route load-check endpoint:
 * how many of an order's required parcels were scanned at the depot, plus the
 * parcel codes that remain unscanned.
 */
export interface LoadCheckOrderStatus {
  order_id: number;
  order_title: string;
  required: number;
  scanned: number;
  unscanned_codes: string[];
}

/**
 * Response from GET /api/v1/routes/{id}/load-check/.
 * Pre-route depot load-check status: required vs scanned parcel-code counts,
 * the tenant strictness mode, the flagged flag (warn mode), and a per-order
 * breakdown listing which parcels remain unscanned.
 */
export interface RouteLoadCheckStatus {
  route_id: number;
  mode: LoadCheckMode;
  flagged: boolean;
  required_count: number;
  scanned_count: number;
  remaining_count: number;
  is_complete: boolean;
  orders: LoadCheckOrderStatus[];
  unscanned_orders: LoadCheckOrderStatus[];
}
